import { Grid, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  AutocompleteInput,
  FunctionField,
  RecordContextProvider,
  ReferenceField,
  ReferenceInput,
  required,
  SelectInput,
  TextInput,
  maxLength,
} from "react-admin";
import { employeeRequests } from "api/employeeApi";
import { getProfessorRecordText } from "server/components/Fields/_fieldsHelper";
import { AttestationStatusChoices, ClassTypeChoices } from "utils/choices";
import { attestationColumns, NOTE_MAX_LENGTH } from "./columns";
import AttestationCell from "./AttestationCell";

const EXTERNALLY_SIGNED_OFF = "Външно заверена";

const AttestationRow = ({ attestation, index, disciplineLabel, canManage, onRemove }) => {
  const source = field => `attestations.${index}.${field}`;
  // A row that has not been saved yet is the only one whose class type is still free to pick,
  // and the only one that can be dropped - the endpoint never deletes attestations.
  const isNew = !attestation.id;
  const classTypeName =
    ClassTypeChoices.find(choice => choice.id === attestation.classTypeId)?.name ?? attestation.classTypeNameBg;
  const statusName = AttestationStatusChoices.find(choice => choice.id === attestation.status)?.name;

  return (
    <Grid
      container
      spacing={1}
      alignItems="center"
      sx={{ py: 0.5 }}
    >
      <AttestationCell
        label={attestationColumns.discipline.label}
        md={attestationColumns.discipline.md}
      >
        {disciplineLabel}
      </AttestationCell>
      <AttestationCell
        label={attestationColumns.classType.label}
        md={attestationColumns.classType.md}
      >
        {canManage && isNew ? (
          <SelectInput
            source={source("classTypeId")}
            label={false}
            emptyText={false}
            choices={ClassTypeChoices}
            validate={[required()]}
            helperText={false}
            fullWidth
          />
        ) : (
          // The class type of an existing attestation cannot be changed - only status, professor and note.
          (classTypeName ?? "-")
        )}
      </AttestationCell>
      <AttestationCell
        label={attestationColumns.status.label}
        md={attestationColumns.status.md}
      >
        {canManage ? (
          <SelectInput
            source={source("status")}
            label={false}
            emptyText={false}
            choices={AttestationStatusChoices}
            validate={[required()]}
            helperText={false}
            fullWidth
          />
        ) : (
          (statusName ?? "-")
        )}
      </AttestationCell>
      <AttestationCell
        label={attestationColumns.professor.label}
        md={attestationColumns.professor.md}
      >
        {canManage ? (
          <ReferenceInput
            source={source("professorEmployeeId")}
            reference={employeeRequests.getEmployeesSelect()}
          >
            <AutocompleteInput
              label={false}
              optionText={choice => (choice?.id === "" ? EXTERNALLY_SIGNED_OFF : getProfessorRecordText(choice))}
              emptyText={EXTERNALLY_SIGNED_OFF}
              filterToQuery={query => (query?.length > 2 ? { name: query } : {})}
              helperText={false}
              fullWidth
            />
          </ReferenceInput>
        ) : attestation.professorEmployeeId ? (
          <RecordContextProvider value={attestation}>
            <ReferenceField
              source="professorEmployeeId"
              reference={employeeRequests.getEmployeesSelect()}
              link={false}
            >
              <FunctionField render={getProfessorRecordText} />
            </ReferenceField>
          </RecordContextProvider>
        ) : (
          EXTERNALLY_SIGNED_OFF
        )}
      </AttestationCell>
      <AttestationCell
        label={attestationColumns.note.label}
        md={attestationColumns.note.md}
      >
        {canManage ? (
          <TextInput
            source={source("note")}
            label={false}
            validate={[maxLength(NOTE_MAX_LENGTH)]}
            helperText={false}
            fullWidth
          />
        ) : (
          attestation.note || "-"
        )}
      </AttestationCell>
      <AttestationCell
        label={attestationColumns.actions.label}
        md={attestationColumns.actions.md}
      >
        {canManage && isNew && (
          <IconButton
            color="error"
            size="small"
            title="Премахни реда"
            onClick={() => onRemove(index)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </AttestationCell>
    </Grid>
  );
};

export default AttestationRow;
